import { Injectable } from '@angular/core';
import { DOWN_ARROW, ESCAPE, UP_ARROW } from '@sb-tools/typescript/constants/keycodes';
import Quill from 'quill';
import { QuillQuickAccessOptions } from '@sb-tools/typescript/models/quill-quick-access-options.model';
import { DropdownOverlayService } from '@sb-tools/typescript/services/dropdown-overlay.service';

@Injectable({
    providedIn: 'root',
})
export class QuillQuickAccessService {
    constructor(private dropdownOverlayService: DropdownOverlayService) {}

    addQuickAccessBindings(quillEditor: Quill, keyboardBindings: { [binding: string]: () => void }): void {
        quillEditor.on('text-change', keyboardBindings.textChangeHandler);
        quillEditor.on('selection-change', keyboardBindings.selectionChangeHandler);

        quillEditor.keyboard.addBinding({ key: 'Enter' }, keyboardBindings.enterHandler);
        quillEditor.keyboard.bindings['Enter'].unshift(quillEditor.keyboard.bindings['Enter'].pop());
        quillEditor.keyboard.addBinding({ key: ESCAPE }, keyboardBindings.escapeHandler);
        quillEditor.keyboard.addBinding({ key: UP_ARROW }, keyboardBindings.upHandler);
        quillEditor.keyboard.addBinding({ key: DOWN_ARROW }, keyboardBindings.downHandler);
    }

    getTextBeforeCursor(quillEditor: Quill, cursorPos: number, maxChars: number): string {
        const startPos = Math.max(0, cursorPos - maxChars);
        const textBeforeCursorPos = quillEditor.getText(startPos, cursorPos - startPos);
        return textBeforeCursorPos;
    }

    hideDropdown(dropdownContainer: HTMLElement): void {
        if (dropdownContainer.hidden) {
            return;
        }

        dropdownContainer.setAttribute('hidden', '');
        dropdownContainer.style.display = 'none';
        dropdownContainer.removeAttribute('style');

        this.dropdownOverlayService.hide();
    }

    initDropdownStyles(dropdownContainer: HTMLElement, options: QuillQuickAccessOptions): void {
        dropdownContainer.style.position = 'absolute';

        if (options.fixContainerToQuill) {
            dropdownContainer.style.width = 'auto';
        }
    }

    scrollActiveDropdownItemToView(
        dropdownElement: HTMLElement,
        itemsListElement: HTMLElement,
        activeItemIndex: number,
    ): void {
        const itemHeight = (itemsListElement.childNodes[activeItemIndex] as HTMLElement).offsetHeight;
        const itemPos = activeItemIndex * itemHeight;
        const containerTop = dropdownElement.scrollTop;
        const containerBottom = containerTop + dropdownElement.offsetHeight;

        if (itemPos < containerTop) {
            // Scroll up if the item is above the top of the container
            dropdownElement.scrollTop = itemPos;
        } else if (itemPos > containerBottom - itemHeight) {
            // scroll down if any part of the element is below the bottom of the container
            dropdownElement.scrollTop += itemPos - containerBottom + itemHeight;
        }
    }

    showDropdown(
        quillEditor: Quill,
        options: QuillQuickAccessOptions,
        itemsCount: number,
        dropdownContainer: HTMLElement,
        charPos: number,
    ): void {
        dropdownContainer.style.visibility = 'hidden';
        dropdownContainer.style.display = '';
        dropdownContainer.scrollTop = 0;

        this.dropdownOverlayService.show();
        this.setDropdownPosition(quillEditor, options, itemsCount, dropdownContainer, charPos);
    }

    private isContainerBottomIsNotVisible(
        dropdownContainer: HTMLElement,
        topPos: number,
        containerPos: DOMRect,
    ): boolean {
        const dropdownContainerBottom = topPos + dropdownContainer.offsetHeight + containerPos.top;
        return dropdownContainerBottom > window.pageYOffset + window.innerHeight;
    }

    private isContainerRightIsNotVisible(
        dropdownContainer: HTMLElement,
        leftPos: number,
        containerPos: DOMRect,
        options: QuillQuickAccessOptions,
    ): boolean {
        if (options.fixContainerToQuill) {
            return false;
        }

        const rightPos = leftPos + dropdownContainer.offsetWidth + containerPos.left;

        if (options.parentContainerId) {
            return rightPos > containerPos.width;
        } else {
            const browserWidth = window.pageXOffset + document.documentElement.clientWidth;
            return rightPos > browserWidth;
        }
    }

    private setDropdownPosition(
        quillEditor: Quill,
        options: QuillQuickAccessOptions,
        itemsCount: number,
        dropdownContainer: HTMLElement,
        charPos: number,
    ): void {
        if (options.isFixedPosition) {
            this.setDropdownPositionFixed(quillEditor, options, dropdownContainer, charPos);
        } else {
            this.setDropdownPositionNormal(quillEditor, options, itemsCount, dropdownContainer, charPos);
        }
    }

    private setDropdownPositionFixed(
        quillEditor: Quill,
        options: QuillQuickAccessOptions,
        dropdownContainer: HTMLElement,
        charPos: number,
    ): void {
        dropdownContainer.removeAttribute('hidden');
        dropdownContainer.style.position = 'fixed';
        dropdownContainer.style.height = null;

        let containerPos;
        let mentionCharPos;

        if (quillEditor) {
            containerPos = quillEditor.container.getBoundingClientRect();
            mentionCharPos = quillEditor.getBounds(charPos);
        } else {
            const parentElement = document.getElementsByClassName('note-editable')[0];
            containerPos = parentElement.getBoundingClientRect();

            mentionCharPos = window.getSelection().getRangeAt(0).getBoundingClientRect();
            mentionCharPos = {
                left: mentionCharPos.left - containerPos.left,
                top: mentionCharPos.top - containerPos.top,
            };
        }
        const mentionCharPosAbsolute = {
            left: containerPos.left + mentionCharPos.left,
            top: containerPos.top + mentionCharPos.top,
            width: 0,
            height: mentionCharPos.height,
        };

        // Which rectangle should it be relative to
        const relativeToPos = options.fixContainerToQuill ? containerPos : mentionCharPosAbsolute;

        const bottomPos =
            window.innerHeight - containerPos.bottom + (containerPos.height - mentionCharPos.top) + options.offsetTop;

        let topPos = options.offsetTop;
        let leftPos = options.offsetLeft;

        // handle horizontal positioning
        if (options.fixContainerToQuill) {
            const rightPos = relativeToPos.right;
            dropdownContainer.style.right = `${rightPos}px`;
        } else {
            leftPos += relativeToPos.left;
            // if it's off the right edge, push it back
            if (leftPos + dropdownContainer.offsetWidth > document.documentElement.clientWidth) {
                leftPos -= leftPos + dropdownContainer.offsetWidth - document.documentElement.clientWidth;
            }
        }

        const availableSpaceTop = relativeToPos.top;
        const availableSpaceBottom = document.documentElement.clientHeight - (relativeToPos.top + relativeToPos.height);

        const fitsBottom = dropdownContainer.offsetHeight <= availableSpaceBottom;
        const fitsTop = dropdownContainer.offsetHeight <= availableSpaceTop;

        let placement;
        if (options.defaultMenuOrientation === 'top' && fitsTop) {
            placement = 'top';
        } else if (options.defaultMenuOrientation === 'bottom' && fitsBottom) {
            placement = 'bottom';
        } else {
            // it doesn't fit either so put it where there's the most space
            placement = availableSpaceBottom > availableSpaceTop ? 'bottom' : 'top';
        }

        if (placement === 'bottom') {
            topPos = relativeToPos.top + relativeToPos.height + options.offsetTop;
            if (!fitsBottom) {
                // shrink it to fit
                // 3 is a bit of a fudge factor so it doesnt touch the edge of the screen
                dropdownContainer.style.height = availableSpaceBottom - 3 + 'px';
            }

            options.dropdownContainerClass.split(' ').forEach((className) => {
                dropdownContainer.classList.add(`${className}-bottom`);
                dropdownContainer.classList.remove(`${className}-top`);
            });

            dropdownContainer.style.bottom = '';
            dropdownContainer.style.top = `${topPos}px`;
        } else {
            options.dropdownContainerClass.split(' ').forEach((className) => {
                dropdownContainer.classList.add(`${className}-top`);
                dropdownContainer.classList.remove(`${className}-bottom`);
            });

            dropdownContainer.style.top = '';
            dropdownContainer.style.bottom = `${bottomPos}px`;
        }

        dropdownContainer.style.left = `${leftPos}px`;
        dropdownContainer.style.visibility = 'visible';
    }

    private setDropdownPositionNormal(
        quillEditor: Quill,
        options: QuillQuickAccessOptions,
        itemsCount: number,
        dropdownContainer: HTMLElement,
        charPos: number,
    ) {
        dropdownContainer.removeAttribute('hidden');
        dropdownContainer.style.position = 'absolute';
        const containerPos = quillEditor.container.getBoundingClientRect();
        const charPosition = quillEditor.getBounds(charPos);
        let containerHeight = dropdownContainer.offsetHeight;

        const containerMinHeight =
            itemsCount * options.dropdownItemHeight > options.dropdownListMaxHeight
                ? options.dropdownListMaxHeight
                : itemsCount * options.dropdownItemHeight;

        if (containerHeight < containerMinHeight) {
            containerHeight = containerMinHeight;
        }

        const bottomPos = containerPos.height - charPosition.top + options.offsetTop;
        let topPos = options.offsetTop;
        let leftPos = options.offsetLeft;

        // handle horizontal positioning
        if (options.fixContainerToQuill) {
            const rightPos = 0;
            dropdownContainer.style.right = `${rightPos}px`;
        } else {
            leftPos += charPosition.left;
        }

        if (this.isContainerRightIsNotVisible(dropdownContainer, leftPos, containerPos, options)) {
            const containerWidth = dropdownContainer.offsetWidth + options.offsetLeft;
            const quillWidth = containerPos.width;
            leftPos = quillWidth - containerWidth;
        }

        // handle vertical positioning
        if (options.defaultMenuOrientation === 'top') {
            // Attempt to align the dropdown container with the top of the quill editor
            if (options.fixContainerToQuill) {
                topPos = -1 * (containerHeight + options.offsetTop);
            } else {
                topPos = charPosition.top - (containerHeight + options.offsetTop);
            }

            // default to bottom if the top is not visible
            if (topPos + containerPos.top <= 0) {
                let overItemsCharPos = options.offsetTop;

                if (options.fixContainerToQuill) {
                    overItemsCharPos += containerPos.height;
                } else {
                    overItemsCharPos += charPosition.bottom;
                }

                topPos = overItemsCharPos;
            }
        } else {
            // Attempt to align the dropdown container with the bottom of the quill editor
            if (options.fixContainerToQuill) {
                topPos += containerPos.height;
            } else {
                topPos += charPosition.bottom;
            }

            // default to the top if the bottom is not visible
            if (this.isContainerBottomIsNotVisible(dropdownContainer, topPos, containerPos)) {
                let overItemsCharPos = options.offsetTop * -1;

                if (!options.fixContainerToQuill) {
                    overItemsCharPos += charPosition.top;
                }

                topPos = overItemsCharPos - containerHeight;
            }
        }

        if (topPos >= 0) {
            options.dropdownContainerClass.split(' ').forEach((className) => {
                dropdownContainer.classList.add(`${className}-bottom`);
                dropdownContainer.classList.remove(`${className}-top`);
                dropdownContainer.style.bottom = 'auto';
                dropdownContainer.style.top = `${topPos}px`;
            });
        } else {
            options.dropdownContainerClass.split(' ').forEach((className) => {
                dropdownContainer.classList.add(`${className}-top`);
                dropdownContainer.classList.remove(`${className}-bottom`);
                dropdownContainer.style.bottom = `${bottomPos}px`;
                dropdownContainer.style.top = 'auto';
            });
        }

        dropdownContainer.style.left = `${leftPos}px`;
        dropdownContainer.style.visibility = 'visible';
    }
}
